import React, { useMemo } from 'react'
import { Badge, Button } from 'react-bootstrap'
import styled from 'styled-components'
import { Content } from '../../types/models/content'

interface ActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: string
  size?: 'sm' | 'lg'
  disabled?: boolean
  children: React.ReactNode
}

const ActionButton = styled(({ variant, size, children, ...props }: ActionButtonProps) => (
  <Button variant={variant} size={size} {...props}>
    {children}
  </Button>
))`
  width: 100%;
  margin-bottom: 0.75rem;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:disabled {
    opacity: 0.6;
  }

  .badge {
    font-size: 0.75rem;
    padding: 0.35em 0.65em;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
`

interface SelectedImagesStatsProps {
  selectedImages: Content[]
  filteredContent: Content[]
  onSelectAll: () => void
  onDeselectAll: () => void
  onInvertSelection: () => void
  setShowDeleteConfirm: (show: boolean) => void
}

const SelectedImagesStats: React.FC<SelectedImagesStatsProps> = ({
  selectedImages,
  filteredContent,
  onSelectAll,
  onDeselectAll,
  onInvertSelection,
  setShowDeleteConfirm,
}) => {
  const datasourceCounts = useMemo(() => {
    return selectedImages.reduce((acc: Record<string, number>, img) => {
      const source = img.metadata?.datasource || 'unknown'
      acc[source] = (acc[source] || 0) + 1
      return acc
    }, {})
  }, [selectedImages])

  const renderActionButtons = () => (
    <div className="btn-group-vertical w-100">
      <ActionButton variant="outline-primary" size="sm" onClick={onSelectAll} type="button">
        <span>
          <i className="bi bi-check2-all me-2"></i>
          Select All Images
        </span>
        <Badge bg="primary">{filteredContent.length}</Badge>
      </ActionButton>
      <ActionButton
        variant="outline-secondary"
        size="sm"
        onClick={onDeselectAll}
        disabled={selectedImages.length === 0}
        type="button"
      >
        <span>
          <i className="bi bi-x-lg me-2"></i>
          Deselect All
        </span>
      </ActionButton>
      <ActionButton variant="outline-info" size="sm" onClick={onInvertSelection} type="button">
        <span>
          <i className="bi bi-arrow-repeat me-2"></i>
          Invert Selection
        </span>
      </ActionButton>
    </div>
  )

  return (
    <div>
      <div className="section">
        <h4>
          <i className="bi bi-gear-fill"></i>
          Content
        </h4>
        {renderActionButtons()}
      </div>

      <div className="section">
        <h4>
          <i className="bi bi-info-circle-fill"></i>
          Selection Details
        </h4>
        <div className="mb-3">
          <div className="stats-label">Selected Images</div>
          <div className="stats-value">
            <span>
              {selectedImages.length} of {filteredContent.length}
            </span>
            {selectedImages.length > 0 && (
              <Button variant="danger" size="sm" onClick={() => setShowDeleteConfirm(true)}>
                <i className="bi bi-trash me-1"></i>
                Delete
              </Button>
            )}
          </div>
          <div className="progress">
            <div
              className="progress-bar bg-primary"
              role="progressbar"
              style={{
                width: `${(selectedImages.length / filteredContent.length) * 100}%`,
              }}
              aria-valuenow={(selectedImages.length / filteredContent.length) * 100}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>
      </div>

      {Object.entries(datasourceCounts).length > 0 && (
        <div className="section">
          <h4>
            <i className="bi bi-diagram-3-fill"></i>
            Source Distribution
          </h4>
          {Object.entries(datasourceCounts).map(([source, count]) => (
            <div key={source} className="mb-2">
              <div className="stats-label">{source}</div>
              <div className="stats-value">
                <span>{count}</span>
                <span className="text-muted">
                  {(((count ?? 0) / (selectedImages.length ?? 1)) * 100).toFixed(1)}%
                </span>
              </div>
              <div className="progress">
                <div
                  className="progress-bar bg-info"
                  role="progressbar"
                  style={{ width: `${(count / selectedImages.length) * 100}%` }}
                  aria-valuenow={(count / selectedImages.length) * 100}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default SelectedImagesStats
